import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import { auth, ensureFirebase } from '../lib/firebase';
import { api } from '../lib/api';

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  isBlocked: boolean;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    ensureFirebase().then(() => {
      unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const result = await api<{ uid: string; isAdmin: boolean; isBlocked: boolean }>(
            '/api/auth/sync',
            { method: 'POST' },
          );
          setIsAdmin(result.isAdmin);
          setIsBlocked(result.isBlocked);
        } catch (err) {
          console.error('Auth sync failed:', err);
          setIsAdmin(false);
          setIsBlocked(false);
        }
      } else {
        setIsAdmin(false);
        setIsBlocked(false);
      }
      setUser(firebaseUser);
      setLoading(false);
      });
    });
    return () => unsubscribe?.();
  }, []);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
  };

  const logout = async () => {
    await signOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, isAdmin, isBlocked, loading, signInWithGoogle, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
